import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Calendar, Inbox, Loader2 } from 'lucide-react';
import { useMyDay } from '@/hooks/use-my-day';
import { MyDayTaskList } from '@/components/my-day/my-day-task-list';
import { MyDayQuickAdd } from '@/components/my-day/my-day-quick-add';
import { TimeBudgetBar } from '@/components/my-day/time-budget-bar';
import { EndOfDayActions } from '@/components/my-day/end-of-day-actions';
import { EmptyState } from '@/components/common/empty-state';
import { MyDaySkeleton } from '@/components/common/skeletons';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { BriefingCard } from '@/components/briefing/briefing-card';

export const Route = createFileRoute('/_authenticated/my-day')({
  component: MyDayPage,
});

function MyDayPage() {
  const { data, isLoading } = useMyDay();
  const { isRefreshing } = usePullToRefresh({ queryKeys: [['my-day']] });

  const today = new Date();
  const formattedDate = format(today, 'EEEE, MMMM d');

  const tasks = data?.tasks ?? [];
  const summary = data?.summary ?? {
    totalMinutes: 0,
    availableMinutes: 480,
    unestimatedCount: 0,
    completedCount: 0,
    totalCount: 0,
  };

  if (isLoading) {
    return <MyDaySkeleton />;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Pull-to-refresh indicator */}
      {isRefreshing && (
        <div className="flex items-center justify-center py-2 md:hidden">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-xs text-muted-foreground">Refreshing...</span>
        </div>
      )}

      {/* Morning Briefing */}
      <BriefingCard />

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Calendar className="h-6 w-6" />
            My Day
          </h1>
          <p className="text-muted-foreground">{formattedDate}</p>
        </div>
        <EndOfDayActions summary={summary} />
      </div>

      {/* Quick Add */}
      <MyDayQuickAdd />

      {/* Time Budget Bar */}
      <TimeBudgetBar summary={summary} />

      {/* Task List */}
      {tasks.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-12 w-12" />}
          title="Your day is clear"
          description="Use the quick add above, or open a task from any project and click 'Add to My Day'."
        />
      ) : (
        <MyDayTaskList tasks={tasks} />
      )}

      {/* Progress summary */}
      {summary.totalCount > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          {summary.completedCount} of {summary.totalCount} tasks done
        </p>
      )}
    </div>
  );
}
